import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'
import { useApi } from '@/lib/auth'
import { usePreviewData } from '../preview-data'
import type { AdminPermission } from './use-admin-permissions'

const tokenSchema = z.object({
  id: z.string(),
  name: z.string(),
  prefixDisplay: z.string(),
  scopes: z.array(z.string()),
  createdBy: z.string(),
  createdAt: z.string(),
  expiresAt: z.string().nullable(),
  lastSeenAt: z.string().nullable(),
  revokedAt: z.string().nullable(),
  status: z.enum(['active', 'revoked', 'expired']),
})
export type ApiTokenView = z.infer<typeof tokenSchema>

// Standard envelope: { code, success, data, errors, messages }
const envelope = <T extends z.ZodType>(data: T) => z.object({ code: z.number().optional(), success: z.boolean(), data })
const listSchema = envelope(z.array(tokenSchema))
const mintedSchema = envelope(z.object({ rawToken: z.string(), token: tokenSchema }))

/** Result of mint/rotate. `rawToken` is shown ONCE: keep it in component state only, never persist or log it. */
export type MintedToken = z.infer<typeof mintedSchema>['data']

export const adminApiTokensQueryKey = ['admin', 'api-tokens'] as const

/** Pure: scopes grouped by permission `groupTag` for the scope editor; unknown scopes land in 'other'. */
export function scopeGroups(
  permissions: AdminPermission[],
  selected: string[] = [],
): { groupTag: string; scopes: { id: string; name: string; selected: boolean }[] }[] {
  const known = new Set(permissions.map((p) => p.id))
  const sel = new Set(selected)
  const groups = new Map<string, { id: string; name: string; selected: boolean }[]>()
  const push = (tag: string, id: string, name: string) =>
    groups.set(tag, [...(groups.get(tag) ?? []), { id, name, selected: sel.has(id) }])
  for (const p of permissions) push(p.groupTag, p.id, p.name)
  for (const s of selected) if (!known.has(s)) push('other', s, s)
  return [...groups.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([groupTag, scopes]) => ({ groupTag, scopes }))
}

/** Pure: which row actions apply: revoke an active token, rotate a non-revoked one, hard-delete only a revoked one. */
export const tokenActions = (t: Pick<ApiTokenView, 'status'>) => ({
  canRevoke: t.status === 'active',
  canRotate: t.status !== 'revoked',
  canDelete: t.status === 'revoked',
})

/**
 * M2M tokens (never includes the secret). `includeRevoked` defaults true (v1 behavior).
 * The scope catalog comes from `useAdminPermissions().data.permissions`.
 */
export function useAdminApiTokens(includeRevoked = true) {
  const api = useApi()
  const preview = usePreviewData()
  return useQuery({
    queryKey: preview ? ['preview', 'admin-api-tokens'] : [...adminApiTokensQueryKey, { includeRevoked }],
    queryFn: async (): Promise<ApiTokenView[]> => {
      const res = await api.get(`/v1/admin/api-tokens?includeRevoked=${includeRevoked}`, listSchema)
      if (!res.success) throw new Error('Failed to fetch API tokens')
      return res.data
    },
    enabled: !preview,
    ...(preview ? { initialData: preview.adminApiTokens, staleTime: Number.POSITIVE_INFINITY } : {}),
  })
}

function useTokenMutation<V, R>(run: (api: ReturnType<typeof useApi>, v: V) => Promise<R>, previewResult: (v: V) => R) {
  const api = useApi()
  const qc = useQueryClient()
  const preview = usePreviewData()
  return useMutation({
    mutationFn: async (v: V): Promise<R> => (preview ? previewResult(v) : run(api, v)),
    onSuccess: async () => {
      if (!preview) await qc.invalidateQueries({ queryKey: adminApiTokensQueryKey })
    },
    // Minted secrets must not linger in the mutation cache.
    gcTime: 0,
  })
}

const PREVIEW_TOKEN: ApiTokenView = {
  id: '00000000-0000-4000-8000-0000000000aa',
  name: 'preview',
  prefixDisplay: 'dvp_preview',
  scopes: [],
  createdBy: 'preview',
  createdAt: '2026-10-15T12:00:00.000Z',
  expiresAt: null,
  lastSeenAt: null,
  revokedAt: null,
  status: 'active',
}

/** `mutateAsync({ name, scopes, expiresAt? })` -> `MintedToken` (POST /v1/admin/api-tokens). `expiresAt` ISO datetime or null. */
export function useCreateApiToken() {
  return useTokenMutation<{ name: string; scopes: string[]; expiresAt?: string | null }, MintedToken>(
    async (api, v) => (await api.request('/v1/admin/api-tokens', { method: 'POST', body: v }, mintedSchema)).data,
    (v) => ({ rawToken: 'preview-raw-token', token: { ...PREVIEW_TOKEN, name: v.name, scopes: v.scopes } }),
  )
}

/** `mutateAsync({ id })` -> `MintedToken` with a NEW secret (POST /v1/admin/api-tokens/:id/rotate); scopes preserved. */
export function useRotateApiToken() {
  return useTokenMutation<{ id: string }, MintedToken>(
    async (api, v) =>
      (await api.request(`/v1/admin/api-tokens/${encodeURIComponent(v.id)}/rotate`, { method: 'POST' }, mintedSchema))
        .data,
    () => ({ rawToken: 'preview-raw-token', token: PREVIEW_TOKEN }),
  )
}

const flagSchema = z.object({ success: z.boolean() }).passthrough()

/** `mutate({ id })` POST /v1/admin/api-tokens/:id/revoke (soft delete; confirm first). */
export function useRevokeApiToken() {
  return useTokenMutation<{ id: string }, unknown>(
    (api, v) => api.request(`/v1/admin/api-tokens/${encodeURIComponent(v.id)}/revoke`, { method: 'POST' }, flagSchema),
    () => ({ success: true }),
  )
}

/** `mutate({ id, scopes })` PATCH /v1/admin/api-tokens/:id/scopes (>=1 scope; secret unchanged). */
export function useUpdateApiTokenScopes() {
  return useTokenMutation<{ id: string; scopes: string[] }, unknown>(
    (api, { id, scopes }) =>
      api.request(
        `/v1/admin/api-tokens/${encodeURIComponent(id)}/scopes`,
        { method: 'PATCH', body: { scopes } },
        flagSchema,
      ),
    () => ({ success: true }),
  )
}

/** `mutate({ id })` DELETE /v1/admin/api-tokens/:id (hard delete; only a revoked token; confirm first). */
export function useDeleteApiToken() {
  return useTokenMutation<{ id: string }, unknown>(
    (api, v) => api.request(`/v1/admin/api-tokens/${encodeURIComponent(v.id)}`, { method: 'DELETE' }, flagSchema),
    () => ({ success: true }),
  )
}
